import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Image } from 'components/lib';
import { OrganizationSummary } from 'services/api';
import { colorInputFieldGray, colorLightGrey } from 'customColors';

export interface OrganizationSelectorProps {
  availableOrganizations: OrganizationSummary[];
  selectedOrganizationId: string;
  displayName: string;
  displayLogoUrl: string;
  onOrganizationChange: (organizationId: string) => void;
}

const OrganizationLogo = ({
  logoUrl,
  name,
}: {
  logoUrl: string;
  name: string;
}): ReactElement => (
  <Box
    sx={{
      width: 50,
      height: 50,
      flexShrink: 0,
      borderRadius: 4,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colorLightGrey,
    }}
  >
    {logoUrl ? (
      <Image
        src={logoUrl}
        alt={name}
        width="100%"
        height="100%"
        style={{ objectFit: 'contain' }}
      />
    ) : (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: colorLightGrey,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: pxToEm(18), fontWeight: 600, color: '#000000' }}>
          {name.charAt(0).toUpperCase()}
        </Typography>
      </Box>
    )}
  </Box>
);

const OrganizationName = ({ name }: { name: string }): ReactElement => (
  <Box flex={1} minWidth={0}>
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: pxToEm(18),
        lineHeight: pxToEm(25),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {name}
    </Typography>
  </Box>
);

export const OrganizationSelector = ({
  availableOrganizations,
  selectedOrganizationId,
  displayName,
  displayLogoUrl,
  onOrganizationChange,
}: OrganizationSelectorProps): ReactElement => {
  if (availableOrganizations.length > 1) {
    return (
      <Box sx={{ mb: 16, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            p: 16,
            borderRadius: 8,
            backgroundColor: colorInputFieldGray,
          }}
        >
          <OrganizationLogo logoUrl={displayLogoUrl} name={displayName} />
          <OrganizationName name={displayName} />
          <Box sx={{ display: 'flex', alignItems: 'center', color: '#383838', flexShrink: 0, width: 24, height: 24 }}>
            <ExpandMore sx={{ fontSize: 24 }} />
          </Box>
        </Box>
        <Box
          component="select"
          value={selectedOrganizationId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onOrganizationChange(e.target.value)
          }
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: 'pointer',
            border: 'none',
            background: 'transparent',
            appearance: 'none',
            font: 'inherit',
            zIndex: 1,
          }}
        >
          {availableOrganizations.map(org => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        p: 16,
        borderRadius: 8,
        backgroundColor: colorInputFieldGray,
        mb: 16,
      }}
    >
      <OrganizationLogo logoUrl={displayLogoUrl} name={displayName} />
      <OrganizationName name={displayName} />
    </Box>
  );
};
